import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import BudgetSummary from "./BudgetSummary";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { RadioGroup } from "../../../UI/RadioGroup";
import { VscChevronDown } from "react-icons/vsc";
import { useMutation } from "react-query";
import { BudgetService } from "../../../../services/budget.service";
import ClientSpreadsheetService from "../../../../services/clientSpreadsheet.service";
import {
  ClientSpreadsheetStage,
  describeClientSpreadsheetExport,
} from "../../../../types/clientSpreadsheet.type";
import { useLocation } from "react-router-dom";
import { useProject } from "../../../../contexts/project.context";
import { groupByAttributes } from "../../../ExportNegotiation";
import { StatusBudget } from "../../../../types/budget/budget";
import { TabActive } from "../../../../types/project.type";
import { FaThumbtack } from "react-icons/fa";
import { ReactComponent as Unpinned } from "../../../../assets/styled-icons/unpinned.svg";
import { hasConsultingBefore } from "../ActiveBudget/ActiveBudget";

type RightSideBarProps = {
  pinned?: boolean;
  pinOutside?: boolean;
};

enum ExportType {
  MM = "MM",
  CLIENT = "CLIENT",
  FULL = "VALUES_DESCRIPTION",
  MM_VALUES = "MM_VALUES",
  CLIENT_CONSULTING = "CLIENT_CONSULTING",
}

enum ExportMode {
  FULL = "FULL",
  SIMPLIFIED = "SIMPLIFIED",
}

enum ExportFormat {
  PDF = "PDF",
  XLX = "XLX",
}

enum ExportSortOrder {
  TOTAL_ASC = "TOTAL_ASC",
  TOTAL_DESC = "TOTAL_DESC",
  CREATION_ORDER = "CREATION_ORDER",
}

/** #MMV-2582: "Por Fornecedor" (estrutura atual) vs. "Por Agrupamento" (nova). */
enum ExportView {
  BY_SUPPLIER = "BY_SUPPLIER",
  BY_GROUP = "BY_GROUP",
}

const exportActiveLabel: { [index: number]: string } = {
  7: "orçamentos",
  8: "negociações",
  9: "fechamentos",
};

const RightSideBar: React.FC<RightSideBarProps> = ({
  pinned = false,
  pinOutside = false,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const projectId = searchParams.get("projectId");
  const {
    budgetList,
    showNotification,
    setNegotiationVersion,
    detailedBudget,
    setDetailedBudget,
    setVersionInfo,
    activeBudget,
    isBudgetDisabled,
    runBudgetAutosave,
    hydrateActiveBudget,
  } = useBudget();
  const { tabActive, project } = useProject();

  const [exportType, setExportType] = useState(ExportType.CLIENT);
  const [exportMode, setExportMode] = useState(ExportMode.SIMPLIFIED);
  const [exportFormat, setExportFormat] = useState(ExportFormat.XLX);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBudgets, setSelectedBudgets] = useState<any[]>([]);
  const [selectedNegotiations, setSelectedNegotiations] = useState<any[]>([]);
  const [selectedNegotiationIds, setSelectedNegotiationIds] = useState<
    string[]
  >([]);
  const [selectedApprovedBudgets, setSelectedApprovedBudgets] = useState<
    string[]
  >([]);
  const [exportSortOrder, setExportSortOrder] = useState<ExportSortOrder>(
    ExportSortOrder.CREATION_ORDER
  );
  const [exportView, setExportView] = useState<ExportView>(
    ExportView.BY_SUPPLIER
  );
  const [exportingClientTemplate, setExportingClientTemplate] = useState(false);

  useEffect(() => {
    if (tabActive === TabActive.BUDGET && exportMode !== ExportMode.FULL) {
      setExportMode(ExportMode.FULL);
    }
  }, [tabActive, exportMode]);

  /**
   * Tenta gerar a planilha usando o template do cliente.
   * Retorna true se usou o template, false se o cliente nao tem template (404) — nesse caso o chamador deve usar o fallback.
   */
  const handleExportWithClientTemplate = async (
    budgetIds: string[],
    stage: ClientSpreadsheetStage,
    negotiationVersion?: number
  ): Promise<boolean> => {
    const clientId = project?.client?._id;
    if (!clientId || !projectId) return false;

    setExportingClientTemplate(true);
    try {
      const meta = await ClientSpreadsheetService.generate(clientId, {
        projectId,
        budgetIds: budgetIds.length > 0 ? budgetIds : undefined,
        stage,
        negotiationVersion,
      });
      const { message, severity } = describeClientSpreadsheetExport(meta);
      showNotification(message, severity);
      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        return false;
      } else if (status === 400) {
        const body = err?.response?.data;
        showNotification(
          body?.message || "Erro ao gerar planilha do cliente.",
          "warning"
        );
        return true;
      } else {
        showNotification(
          "Nao foi possivel gerar a planilha do cliente.",
          "warning"
        );
        return true;
      }
    } finally {
      setExportingClientTemplate(false);
    }
  };

  // Ordering is a budget-only feature. Other stages always use creation order.
  const currentExportSortOrder =
    tabActive === TabActive.BUDGET
      ? exportSortOrder
      : ExportSortOrder.CREATION_ORDER;
  const openMenu = Boolean(anchorEl);

  const handleChangeDetailedBudget = useCallback(
    (identifier: string, version: number) => {
      const budgetId = identifier.split("-")[0];
      const newActiveBudget = budgetList?.find((el) => el._id === budgetId);
      const needsHydration =
        !!newActiveBudget?._id &&
        (newActiveBudget._id !== activeBudget?._id ||
          !Array.isArray(activeBudget?.budgetServices));

      // A etapa da linha, calculada antes do hydrate. O setState só vale no
      // próximo render; o recálculo deste clique precisa dela agora, senão
      // o Fechamento abre com a árvore do Orçamento (tela em R$ 0,00).
      const requestedVersion = !isNaN(version) ? version : -1;
      const stageVersion =
        requestedVersion >= 0
          ? newActiveBudget?.versionHistory.find(
              (el) => el.version === requestedVersion
            )
          : tabActive === TabActive.NEGOTIATION
          ? newActiveBudget?.versionHistory.find(
              (el) => el.status === "NEGOTIATE"
            )
          : tabActive === TabActive.APPROVED
          ? newActiveBudget?.versionHistory.find(
              (el) => el.status === "APPROVED" || el.status === "DENIED_CLOSING"
            )
          : {
              comparisonVersion: 0,
              version: 0,
              status: "",
            };

      // FE-P1-03 / FE-P1-07: shared hydrate path. Same row re-select/expand still
      // hydrates when services are missing (do not early-return before this).
      if (needsHydration && newActiveBudget) {
        void hydrateActiveBudget(newActiveBudget, stageVersion);
      }

      if (identifier === detailedBudget) return;

      // se receber version adicionar version,
      if (!isNaN(version)) {
        setNegotiationVersion(version);
        if (stageVersion && requestedVersion >= 0) setVersionInfo(stageVersion);
      }
      // se não receber version avaliar version automatica para mudança de tab
      if (version === -1 || isNaN(version)) {
        switch (tabActive) {
          case TabActive.BUDGET:
            setNegotiationVersion(0);
            setVersionInfo({
              comparisonVersion: 0,
              version: 0,
              status: "",
            });
            break;
          case TabActive.NEGOTIATION:
            const firstNegVersion = newActiveBudget?.versionHistory.find(
              (el) => el.status === "NEGOTIATE"
            );
            if (firstNegVersion) {
              setNegotiationVersion(firstNegVersion.version);
              setVersionInfo(firstNegVersion);
            }
            break;
          case TabActive.APPROVED:
            const firstApprVersion = newActiveBudget?.versionHistory.find(
              (el) => el.status === "APPROVED" || el.status === "DENIED_CLOSING"
            );
            if (firstApprVersion) {
              setNegotiationVersion(firstApprVersion.version);
              setVersionInfo(firstApprVersion);
            }
            break;
        }
      }
      setDetailedBudget(identifier);
    },
    [
      activeBudget?._id,
      activeBudget?.budgetServices,
      budgetList,
      detailedBudget,
      hydrateActiveBudget,
      setDetailedBudget,
      setNegotiationVersion,
      setVersionInfo,
      tabActive,
    ]
  );

  const { mutate: exportBudget } = useMutation(
    (data: any) => BudgetService.exportXLX(data),
    {
      onSuccess: (data) => {
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        const budgetName =
          budgetList?.find((el) => el._id === selectedBudgets[0])?.name || "";
        const fileName = project?.mmCode
          ? `MM${project?.mmCode} - ${project?.event?.name} - ${
              selectedBudgets.length > 1 ? "Orçamentos" : budgetName
            }.xlsx`
          : "PrevisaodeCustos.xlsx";
        link.setAttribute("download", fileName);
        link.href = URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showNotification("Exportado com sucesso!", "success");
      },
      onError: () => {
        showNotification("Não foi possível exportar", "warning");
      },
    }
  );

  const { mutate: exportNegotiationBudget } = useMutation(
    (data: any) => BudgetService.exportNegotiationXLX(data),
    {
      onSuccess: (data) => {
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        const fileName = project?.mmCode
          ? `MM${project?.mmCode} - ${project?.event?.name} - ${
              selectedNegotiationIds.length > 1 ? "Negociações" : "Negociação"
            }.xlsx`
          : "PrevisaodeCustos.xlsx";

        link.setAttribute("download", fileName);
        link.href = URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showNotification("Exportado com sucesso!", "success");
      },
      onError: () => {
        showNotification("Não foi possível exportar", "warning");
      },
    }
  );

  const { mutate: exportApprovedBudget } = useMutation(
    (data: any) => BudgetService.exportApprovedXLX(data),
    {
      onSuccess: (data) => {
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        const fileName = project?.mmCode
          ? `MM${project?.mmCode} - ${project?.event?.name} - ${
              selectedApprovedBudgets.length > 1
                ? "Fechamento Consolidado"
                : "Fechamento"
            }.xlsx`
          : "PrevisaodeCustos.xlsx";

        link.setAttribute("download", fileName);
        link.href = URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showNotification("Exportado com sucesso!", "success");
      },
      onError: () => {
        showNotification("Não foi possível exportar", "warning");
      },
    }
  );

  /**
   * FE-P1-04 / FE-P1-07: pass list rows (summary snapshots) into accordion.
   * Expanded active row merges `activeBudget` inside BudgetSummary via
   * `budgetForRow` so collapsed totals stay autosave snapshots while
   * expanded categories mirror live calc. Non-active rows never expose
   * `budgetServices` when v2 on. Tabs still iterate `versionHistory`.
   */
  const renderBudgetSumaries = useMemo(() => {
    if (!!budgetList && budgetList.length > 0) {
      switch (tabActive) {
        case TabActive.NEGOTIATION: {
          const summaries = [];
          for (let j = 0; j < budgetList.length; j++) {
            const listRow = budgetList[j];
            const lastNegVersion = listRow.versionHistory
              .filter((v) => v.status === "NEGOTIATE" && !v.isConsulting)
              .reduce((max, v) => (v.version > max ? v.version : max), 0);
            for (let savedVersion of listRow.versionHistory) {
              const budgetId = listRow._id ?? `budget-${j}`;
              const val =
                savedVersion.version > 0
                  ? `${budgetId}-version-${savedVersion.version}`
                  : `${budgetId}-version-0`;
              if (
                savedVersion.version === 0 ||
                !["NEGOTIATE", "DENIED"].includes(savedVersion.status)
              )
                continue;
              summaries.push(
                <BudgetSummary
                  key={val}
                  onChangeDetailed={handleChangeDetailedBudget}
                  val={val}
                  budget={listRow}
                  index={j}
                  version={savedVersion.version}
                  status={savedVersion.status as StatusBudget}
                  isLastNegotiation={
                    savedVersion.version === lastNegVersion &&
                    !savedVersion.isConsulting
                  }
                />
              );
            }
          }
          return summaries;
        }
        case TabActive.APPROVED:
          return budgetList.map((el, index) => {
            const versionAppr = el.versionHistory.find(
              (version) =>
                version.status === "APPROVED" ||
                version.status === "DENIED_CLOSING"
            );
            if (versionAppr) {
              const summaryKey = el._id
                ? `${el._id}-version-${versionAppr.version}`
                : `budget-${index}-version-${versionAppr.version}`;
              const summaryVal = el._id
                ? `${el._id}-version-${versionAppr.version}`
                : summaryKey;
              return (
                <Box key={summaryKey}>
                  <BudgetSummary
                    onChangeDetailed={handleChangeDetailedBudget}
                    val={summaryVal}
                    budget={el}
                    index={index}
                    version={versionAppr?.version}
                    status={versionAppr?.status as StatusBudget}
                  />
                </Box>
              );
            } else return null;
          });
        case TabActive.BUDGET:
        default:
          return budgetList.map((el, index) => {
            if (
              ["DELETED", "APPROVED"].includes(
                el.versionHistory?.[0]?.status || ""
              ) ||
              el === undefined
            )
              return null;
            else
              return (
                <BudgetSummary
                  key={el._id ? `${el._id}-version-0` : `budget-${index}`}
                  onChangeDetailed={handleChangeDetailedBudget}
                  budget={el}
                  index={index}
                  version={0}
                  status={
                    (el.versionHistory?.[0]?.status as StatusBudget) || "OPEN"
                  }
                  val={
                    el._id ? `${el._id}-version-0` : `budget-${index}-version-0`
                  }
                />
              );
          });
      }
    }
    return null;
  }, [budgetList, handleChangeDetailedBudget, tabActive]);

  const allBudgetsSelected = useMemo(() => {
    if (tabActive === TabActive.APPROVED) {
      const comparingList = budgetList?.filter((i) =>
        i.versionHistory.find((version) => version.status === "APPROVED")
      );
      return selectedBudgets?.length === comparingList?.length;
    } else if (tabActive === TabActive.BUDGET) {
      const comparingList = budgetList?.filter(
        (i) =>
          i?.status &&
          !(
            ["NEGOTIATE", "APPROVED", "EXCLUDED", "DELETED"].includes(
              i?.status
            ) || ["DELETED", "APPROVED"].includes(i?.versionHistory?.[0].status)
          )
      );
      return selectedBudgets?.length === comparingList?.length;
    }
  }, [budgetList, selectedBudgets, tabActive]);

  const handleCheckBudget = (budgetId: string | undefined) => {
    if (selectedBudgets.includes(budgetId)) {
      const newBudgets = selectedBudgets.filter((item) => item !== budgetId);
      setSelectedBudgets(newBudgets);
    } else {
      setSelectedBudgets([...selectedBudgets, budgetId]);
    }
  };

  const handleCheckAllBudgets = () => {
    if (allBudgetsSelected) {
      setSelectedBudgets([]);
    } else {
      if (tabActive === TabActive.APPROVED) {
        const allBudgets = budgetList
          ?.filter(
            (i) =>
              i.status &&
              i.versionHistory.find((version) => version.status === "APPROVED")
          )
          ?.map((item) => item._id);
        if (allBudgets) setSelectedBudgets(allBudgets);
      } else {
        const allBudgets = budgetList
          ?.filter(
            (i) =>
              i.status &&
              !(
                ["NEGOTIATE", "APPROVED", "EXCLUDED", "DELETED"].includes(
                  i?.status
                ) ||
                ["DELETED", "APPROVED"].includes(i.versionHistory?.[0].status)
              )
          )
          ?.map((item) => item._id);
        if (allBudgets) setSelectedBudgets(allBudgets);
      }
    }
  };

  const handleExportSelected = async () => {
    if (!selectedBudgets.length) return;
    await runBudgetAutosave("manual");
    const budgets = selectedBudgets.join(",");
    const isClosing = tabActive === TabActive.APPROVED;
    const effectiveExportMode =
      tabActive === TabActive.BUDGET ? undefined : exportMode;

    const exportTypeQuery =
      exportType === ExportType.MM
        ? "mm"
        : exportType === ExportType.MM_VALUES
        ? "mm_values"
        : exportType === ExportType.FULL
        ? "full"
        : ExportType.CLIENT;

    if (exportFormat === ExportFormat.XLX) {
      const usedTemplate = await handleExportWithClientTemplate(
        selectedBudgets,
        "BUDGET"
      );
      if (usedTemplate) return;
      // fallback: cliente sem template — usa exportacao padrao
      exportBudget({
        ids: budgets,
        exportType: exportTypeQuery,
        exportMode: effectiveExportMode,
        sortOrder: currentExportSortOrder,
        view: exportView,
      });
      return;
    }

    if (allBudgetsSelected) {
      window.open(
        `/export?type=${exportTypeQuery}&projectId=${projectId}&status=${
          isClosing ? "APPROVED" : "BUDGET"
        }`,
        "_blank"
      );
    } else {
      window.open(
        `/export?type=${exportTypeQuery}&projectId=${projectId}&budgets=${budgets}&status=${
          isClosing ? "APPROVED" : "BUDGET"
        }`,
        "_blank"
      );
    }
  };

  const handleCheckNegotiation = (negotiationId: string) => {
    if (selectedNegotiationIds.includes(negotiationId)) {
      setSelectedNegotiationIds(
        selectedNegotiationIds.filter((id) => id !== negotiationId)
      );
      return;
    }
    setSelectedNegotiationIds([...selectedNegotiationIds, negotiationId]);
  };

  const handleCheckApprovedBudget = (budgetId: string | undefined) => {
    if (!budgetId) return;
    if (selectedApprovedBudgets.includes(budgetId)) {
      setSelectedApprovedBudgets(
        selectedApprovedBudgets.filter((id) => id !== budgetId)
      );
      return;
    }
    setSelectedApprovedBudgets([...selectedApprovedBudgets, budgetId]);
  };

  const handleExportNegotiation = async () => {
    if (!selectedNegotiationIds.length) return;
    await runBudgetAutosave("manual");

    const checkedNegs = selectedNegotiations.filter((item) =>
      selectedNegotiationIds.includes(`${item.id}-version-${item.version}`)
    );
    if (!checkedNegs.length) return;

    const selections = checkedNegs.map((item) => ({
      budgetId: item.id,
      negotiationVersion: item.version + 1,
    }));

    const exportTypeQuery =
      exportType === ExportType.MM
        ? "mm"
        : exportType === ExportType.FULL
        ? "full"
        : ExportType.CLIENT;

    if (exportFormat === ExportFormat.XLX) {
      if (selections.length === 1) {
        const usedTemplate = await handleExportWithClientTemplate(
          [selections[0].budgetId],
          "NEGOTIATION",
          selections[0].negotiationVersion
        );
        if (usedTemplate) return;
        exportNegotiationBudget({
          exportType: exportType,
          exportMode,
          sortOrder: currentExportSortOrder,
          view: exportView,
          budget: selections[0],
        });
        return;
      }

      exportNegotiationBudget({
        exportType: exportType,
        exportMode,
        sortOrder: currentExportSortOrder,
        view: exportView,
        budgets: selections,
      });
      return;
    }

    const finalQuery = checkedNegs
      .map((item) => `${item.id}-${item.version + 1}`)
      .join(",");
    window.open(
      `/export?type=${exportTypeQuery}&projectId=${projectId}&status=NEGOTIATE&budgets=${finalQuery}`,
      "_blank"
    );
  };

  const handleExportApproved = async () => {
    if (!selectedApprovedBudgets.length) return;
    await runBudgetAutosave("manual");

    const exportTypeQuery =
      exportType === ExportType.MM
        ? "mm"
        : exportType === ExportType.FULL
        ? "full"
        : ExportType.CLIENT;

    if (exportFormat === ExportFormat.XLX) {
      const usedTemplate = await handleExportWithClientTemplate(
        selectedApprovedBudgets,
        "APPROVED"
      );
      if (usedTemplate) return;
      // fallback: cliente sem template — usa exportacao padrao
      exportApprovedBudget({
        exportType: exportType,
        exportMode,
        sortOrder: currentExportSortOrder,
        view: exportView,
        budgetId:
          selectedApprovedBudgets.length === 1
            ? selectedApprovedBudgets[0]
            : undefined,
        budgetIds:
          selectedApprovedBudgets.length > 1
            ? selectedApprovedBudgets
            : undefined,
      });
      return;
    }

    window.open(
      `/export?type=${exportTypeQuery}&projectId=${projectId}&budgets=${selectedApprovedBudgets.join(
        ","
      )}&status=APPROVED`,
      "_blank"
    );
  };

  useEffect(() => {
    if (!!budgetList?.length) {
      let list: any[] = [];

      for (let j = 0; j < budgetList.length; j++) {
        const lastNegVersion = budgetList[j].versionHistory
          .filter((v) => v.status === "NEGOTIATE" && !v.isConsulting)
          .reduce((max, v) => (v.version > max ? v.version : max), 0);
        if (lastNegVersion === 0) continue;

        const savedVersion = budgetList[j].versionHistory.find(
          (v) => v.version === lastNegVersion
        );
        if (!savedVersion) continue;

        const shouldDecrementIndex = hasConsultingBefore(
          budgetList[j].versionHistory,
          savedVersion.version
        );
        list.push({
          id: budgetList[j]._id,
          budget: j,
          index: j,
          version: savedVersion.version - 1,
          name: budgetList[j]?.name,
          hasConsultingBefore: shouldDecrementIndex,
        });
      }

      setSelectedNegotiations(list);
    }
  }, [budgetList, tabActive]);

  const buildExportOptions = () => {
    if (tabActive === TabActive.NEGOTIATION) {
      return (
        <div>
          {selectedNegotiations.map((item) => {
            const negotiationId = `${item.id}-version-${item.version}`;
            const label = item?.name
              ? `${item.name} - Negociação ${
                  item.hasConsultingBefore ? item.version : item.version + 1
                }`
              : `Orçamento ${item.budget + 1} - Negociação ${
                  item.hasConsultingBefore ? item.version : item.version + 1
                }`;
            return (
              <MenuItem key={negotiationId}>
                <div
                  style={{
                    paddingRight: 16,
                    alignItems: "center",
                    display: "flex",
                    cursor: "pointer",
                    height: 20,
                    justifyContent: "flex-start",
                  }}
                  onClick={() => handleCheckNegotiation(negotiationId)}
                >
                  <Checkbox
                    checked={selectedNegotiationIds.includes(negotiationId)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleCheckNegotiation(negotiationId)}
                  />
                  <span>{label}</span>
                </div>
              </MenuItem>
            );
          })}

          <MenuItem>
            <Button
              sx={{ fontSize: "14px", width: "100%" }}
              onClick={handleExportNegotiation}
              disabled={!selectedNegotiationIds.length}
            >
              Confirmar
            </Button>
          </MenuItem>
        </div>
      );
    } else if (tabActive === TabActive.APPROVED) {
      return (
        <div>
          {budgetList?.map((budget) => {
            const approvedVersion = budget.versionHistory.find(
              (version) => version.status === "APPROVED"
            );
            if (!approvedVersion) return null;
            return (
              <MenuItem key={budget._id}>
                <div
                  style={{
                    paddingRight: 16,
                    alignItems: "center",
                    display: "flex",
                    cursor: "pointer",
                    height: 20,
                    justifyContent: "flex-start",
                  }}
                  onClick={() => handleCheckApprovedBudget(budget._id)}
                >
                  <Checkbox
                    checked={selectedApprovedBudgets.includes(budget._id || "")}
                    onChange={() => handleCheckApprovedBudget(budget._id)}
                  />
                  <span>
                    {budget.name || `Orçamento ${budget.budgetIndex + 1}`}
                  </span>
                </div>
              </MenuItem>
            );
          })}

          <MenuItem>
            <Button
              sx={{ fontSize: "14px", width: "100%" }}
              onClick={handleExportApproved}
              disabled={!selectedApprovedBudgets.length}
            >
              Confirmar
            </Button>
          </MenuItem>
        </div>
      );
    } else {
      return (
        <div>
          <MenuItem
            style={{
              paddingRight: 16,
              alignItems: "center",
              display: "flex",
              cursor: "pointer",
              height: 36,
            }}
            onClick={handleCheckAllBudgets}
          >
            <Checkbox
              checked={allBudgetsSelected}
              onClick={handleCheckAllBudgets}
            />
            <span>Todos</span>
          </MenuItem>

          {budgetList?.map((budget) => {
            if (
              (budget.status &&
                ["NEGOTIATE", "APPROVED", "EXCLUDED", "DELETED"].includes(
                  budget.status
                )) ||
              ["DELETED", "APPROVED"].includes(
                budget.versionHistory?.[0].status
              )
            )
              return <></>;
            else
              return (
                <MenuItem>
                  <div
                    style={{
                      paddingRight: 16,
                      alignItems: "center",
                      display: "flex",
                      cursor: "pointer",
                      height: 20,
                      justifyContent: "flex-start",
                    }}
                    onClick={() => handleCheckBudget(budget._id)}
                  >
                    <Checkbox
                      checked={selectedBudgets.includes(budget._id)}
                      onChange={() => handleCheckBudget(budget._id)}
                    />
                    <span>
                      {budget.name || `Orçamento ${budget.budgetIndex + 1}`}
                    </span>
                  </div>
                </MenuItem>
              );
          })}

          <MenuItem>
            <Button
              sx={{ fontSize: "14px", width: "100%" }}
              onClick={handleExportSelected}
            >
              Confirmar
            </Button>
          </MenuItem>
        </div>
      );
    }
  };

  const togglePin = () => {
    const next = !pinned;
    window.dispatchEvent(
      new CustomEvent("rightSidebarPinned", { detail: { pinned: next } })
    );
  };

  const pinButton = !pinOutside && (
    <Box
      onClick={togglePin}
      title={pinned ? "Unpin sidebar" : "Pin sidebar"}
      sx={{
        alignSelf: "center",
        height: "50%",
        width: 14,
        flexShrink: 0,
        backgroundColor: pinned ? "#1361A4" : "#536574",
        marginTop: "24px",
        marginBottom: "24px",
        cursor: "pointer",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        borderTopLeftRadius: "8px",
        borderBottomLeftRadius: "8px",
      }}
    >
      {pinned ? <FaThumbtack color="white" size={14} /> : <Unpinned />}
    </Box>
  );

  return (
    <Box
      sx={{
        flexDirection: "row",
        justifyContent: "flex-start",
        display: "flex",
        flex: 1,
        minHeight: 0,
      }}
    >
      {pinButton}
      <Box
        padding={"16px 16px 16px 2px"}
        zIndex={999}
        bgcolor={"white"}
        sx={{ flex: 1, minWidth: 0, minHeight: 0 }}
      >
        <>
          <span style={{ fontSize: "20px", marginLeft: "10px" }}>
            Lista de Orçamentos
          </span>
          <Box my={2} overflow={"auto"} sx={{ backgroundColor: "#F9FCFF" }}>
            <FormControl fullWidth>
              <RadioGroup.Root
                label=""
                value={detailedBudget}
                onChange={(e, value) => {
                  const version = e.target.name ? Number(e.target.name) : -1;
                  handleChangeDetailedBudget(value, version);
                }}
              >
                {renderBudgetSumaries}
              </RadioGroup.Root>
            </FormControl>
          </Box>

          <Box
            p={"6px 10px"}
            border={"#DBDADE 1px solid"}
            borderRadius={1}
            mt={3}
            display={budgetList?.length! > 0 ? "" : "none"}
          >
            <Box pb={1}>
              <Typography variant={"body1"} style={{ marginTop: 8 }}>
                Exportar {exportActiveLabel[tabActive] || ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FormControl>
                <FormLabel sx={{ fontSize: 14, mt: 1.5, color: "black" }}>
                  Versões
                </FormLabel>

                <RadioGroup.Root
                  label=""
                  value={exportType}
                  onChange={(_e, value) => {
                    setExportType(value as ExportType);
                  }}
                >
                  <RadioGroup.Item
                    label={"Cliente"}
                    value={ExportType.CLIENT}
                  />
                  {tabActive !== TabActive.BUDGET && (
                    <RadioGroup.Item
                      label={"Cliente/Assessoria"}
                      value={ExportType.CLIENT_CONSULTING}
                    />
                  )}
                </RadioGroup.Root>

                {exportFormat === ExportFormat.XLX &&
                  tabActive !== TabActive.BUDGET && (
                    <>
                      <FormLabel sx={{ fontSize: 14, mt: 1.2, color: "black" }}>
                        Modelo
                      </FormLabel>
                      <RadioGroup.Root
                        label={""}
                        value={exportMode}
                        onChange={(_e, value) => {
                          setExportMode(value as ExportMode);
                        }}
                      >
                        <RadioGroup.Item
                          label={"Completo"}
                          value={ExportMode.FULL}
                        />
                        <RadioGroup.Item
                          label={"Simplificado"}
                          value={ExportMode.SIMPLIFIED}
                        />
                      </RadioGroup.Root>
                    </>
                  )}
              </FormControl>

              {/* #MMV-2582: "Formato" removido da tela -- só existe um
                  formato disponível (Planilha), sem necessidade de
                  escolher. `exportFormat` continua fixo em XLX (estado
                  inicial), só a UI que sumiu. */}

              <FormControl>
                <FormLabel sx={{ fontSize: 14, mt: 1.2, color: "black" }}>
                  Visão
                </FormLabel>
                <RadioGroup.Root
                  label=""
                  value={exportView}
                  onChange={(_e, value) => setExportView(value as ExportView)}
                >
                  <RadioGroup.Item
                    label={"Por Fornecedor"}
                    value={ExportView.BY_SUPPLIER}
                  />
                  <RadioGroup.Item
                    label={"Por Agrupamento"}
                    value={ExportView.BY_GROUP}
                  />
                </RadioGroup.Root>
              </FormControl>
              {tabActive === TabActive.BUDGET && (
                <FormControl sx={{ width: "100%", mt: 1 }}>
                  <FormLabel sx={{ fontSize: 14, color: "black" }}>
                    Ordenar exportação
                  </FormLabel>
                  <RadioGroup.Root
                    label=""
                    value={exportSortOrder}
                    onChange={(_e, value) =>
                      setExportSortOrder(value as ExportSortOrder)
                    }
                  >
                    <RadioGroup.Item
                      label="Ordem de Criação (padrão)"
                      value={ExportSortOrder.CREATION_ORDER}
                    />
                    <RadioGroup.Item
                      label="Do maior para o menor"
                      value={ExportSortOrder.TOTAL_DESC}
                    />
                    <RadioGroup.Item
                      label="Do menor para o maior"
                      value={ExportSortOrder.TOTAL_ASC}
                    />
                  </RadioGroup.Root>
                </FormControl>
              )}
            </Box>

            <Button
              aria-controls={openMenu ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={openMenu ? "true" : undefined}
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setSelectedBudgets([]);
                setSelectedNegotiationIds([]);
                setSelectedApprovedBudgets([]);
              }}
              endIcon={exportingClientTemplate ? undefined : <VscChevronDown />}
              sx={{ fontSize: "14px", width: "100%", mt: 2, mb: 1, height: 36 }}
              variant="outlined"
              disabled={exportingClientTemplate}
            >
              {exportingClientTemplate ? "Gerando..." : "Exportar"}
            </Button>

            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={() => setAnchorEl(null)}
              MenuListProps={{ "aria-labelledby": "basic-button" }}
            >
              {buildExportOptions()}
            </Menu>
          </Box>
        </>
      </Box>
    </Box>
  );
};

/** Botão de fixar para uso fora do RightSideBar (à esquerda do container com borda) */
export const RightSideBarPinButton: React.FC<{
  pinned: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}> = ({ pinned, onClick, onMouseEnter, onMouseLeave }) => (
  <Box
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    title={pinned ? "Unpin sidebar" : "Pin sidebar"}
    sx={{
      alignSelf: "center",
      height: "50%",
      width: 14,
      flexShrink: 0,
      backgroundColor: pinned ? "#1361A4" : "#536574",
      marginTop: "24px",
      marginBottom: "24px",
      cursor: "pointer",
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
    }}
  >
    {pinned ? <FaThumbtack color="white" size={14} /> : <Unpinned />}
  </Box>
);

export default RightSideBar;
